// Grammar article reader.
//
//   dashboard → click an article card → article view (params.slug)
//
// Public content (guest-readable) overlaid with the owner's workspace row so the
// pin toggle + personal notes (or the guest CTA) appear under each article.
import { fetchArticles, fetchWorkspace, overlay } from "../lib/data.js";
import { renderMarkdown } from "../ui/markdown.js";
import { hydrateFigures } from "../ui/figures.js";
import { buildRegistry } from "../cms/registry.js";
import { applyAutoLinks } from "../cms/autolink.js";
import { computeSeeAlso, seeAlsoHTML } from "../cms/backlinks.js";
import { renderNotesPin } from "../ui/notesPin.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { navigate, navigateBack } from "../router/router.js";
import { escapeHtml } from "../ui/helpers.js";

const LEVEL_CLASS = {
  A1: "bg-sage-100 text-sage-700",
  A2: "bg-amber-100 text-amber-700",
  B1: "bg-rose-100 text-rose-700",
  B2: "bg-violet-100 text-violet-700",
};

const CATEGORY_LABEL = {
  start: "Start here",
  verbs: "Verbs",
  nouns: "Nouns",
  structure: "Structure",
  articles: "Articles",
  sounds: "Sounds",
  words: "Words",
  sentences: "Sentences",
  prepositions: "Prepositions",
};

export async function articleView(app, params = {}) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading article…</div>`;

  const [articles, workspace] = await Promise.all([fetchArticles(), fetchWorkspace()]);
  const overlaid = overlay(articles, workspace, "grammar_articles");
  const bySlug = new Map(overlaid.map((a) => [a.slug, a]));
  const article = bySlug.get(params.slug);

  if (!article) {
    app.innerHTML = `
      <div class="max-w-2xl mx-auto py-16 text-center">
        <p class="text-3xl mb-3">📭</p>
        <p class="text-sage-700 font-medium">That article isn't available yet.</p>
        <button data-back class="mt-4 text-sm font-medium text-sage-600 hover:text-sage-800">← Back to Grammar Map</button>
      </div>`;
    app.querySelector("[data-back]").addEventListener("click", () => navigate("dashboard"));
    return;
  }

  const prereqSlugs = (article.prerequisites_csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const prereqHtml = prereqSlugs.length
    ? `<div class="flex flex-wrap items-center gap-2 mt-3">
         <span class="text-xs text-sage-400 uppercase tracking-wide">Prerequisites:</span>
         ${prereqSlugs
           .map((slug) => {
             const known = bySlug.get(slug);
             const label = known ? escapeHtml(known.title) : slug;
             return known
               ? `<button data-prereq="${escapeHtml(slug)}" class="text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-sage-200 text-sage-600 hover:border-sage-400">${label}</button>`
               : `<span class="text-xs px-2.5 py-1 rounded-full bg-sage-50 text-sage-400">${escapeHtml(slug)}</span>`;
           })
           .join("")}
       </div>`
    : "";

  const level = article.difficulty_level || "A1";

  app.innerHTML = `
    <article class="max-w-3xl mx-auto fade-in">
      <button data-back class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← Back</button>
      <header class="mb-6 pb-5 border-b border-sage-100">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full ${LEVEL_CLASS[level] || LEVEL_CLASS.A1}">${level}</span>
          <span class="text-[11px] px-2 py-0.5 rounded-full bg-sand-100 text-sand-700">${CATEGORY_LABEL[article.category] || article.category || ""}</span>
          ${article.is_pinned ? '<span class="text-[11px]" title="Pinned to your workspace">📌</span>' : ""}
        </div>
        <h1 class="text-3xl font-bold text-sage-800">${escapeHtml(article.title)}</h1>
        ${prereqHtml}
      </header>
      <div class="prose-dourado" data-body></div>
      <div class="mt-8" data-notes></div>
    </article>`;

  const bodyEl = app.querySelector("[data-body]");
  bodyEl.innerHTML = renderMarkdown(article.content_markdown || "");
  // Swap any [[figure:ID]] slots for their trusted, code-authored SVGs.
  hydrateFigures(bodyEl);
  // Connectedness: auto-link the first mention of any other article / app
  // section, then list what links back here. Both derived from the live set, so
  // new content connects itself with no back-editing. Wrapped so a glitch in the
  // linker can never blank the article body.
  try {
    applyAutoLinks(bodyEl, buildRegistry(articles, article.slug));
  } catch (_) {
    /* auto-linking is an enhancement, never load-bearing */
  }
  const seeAlso = computeSeeAlso(articles, article);
  if (seeAlso.length) {
    const wrap = document.createElement("div");
    wrap.innerHTML = seeAlsoHTML(seeAlso);
    if (wrap.firstElementChild) bodyEl.insertAdjacentElement("afterend", wrap.firstElementChild);
  }

  app.querySelector("[data-back]").addEventListener("click", () => navigateBack("dashboard"));
  app.querySelectorAll("[data-prereq]").forEach((b) =>
    b.addEventListener("click", () => navigate("article", { slug: b.dataset.prereq }))
  );
  // In-body cross-links authored as [label](article:slug) in the markdown.
  app.querySelectorAll("[data-article-link]").forEach((a) =>
    a.addEventListener("click", (e) => {
      e.preventDefault();
      navigate("article", { slug: a.dataset.articleLink });
    })
  );
  // In-body links to a live app view, authored as [label](view:route).
  app.querySelectorAll("[data-view-link]").forEach((a) =>
    a.addEventListener("click", (e) => {
      e.preventDefault();
      navigate(a.dataset.viewLink);
    })
  );

  app
    .querySelector("[data-notes]")
    .appendChild(renderNotesPin(article, "grammar_articles", () => openAuthPanel("signin")));

  window.scrollTo({ top: 0, behavior: "smooth" });
}
